import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { LanguageSwitch } from "../layout/LanguageSwitch";
import { Logo } from "../ui/Logo";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { isV2 } from "../../lib/config";

export function LoginScreen() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const { toastError } = useToast();
  const [tenant, setTenant] = useState(() => { try { return localStorage.getItem("astro.tenant") ?? ""; } catch { return ""; } });
  const [username, setUsername] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await login(username.trim(), pin, tenant.trim().toLowerCase());
      if (isV2()) { try { localStorage.setItem("astro.tenant", tenant.trim().toLowerCase()); } catch { /* storage unavailable */ } }
    } catch (err) {
      toastError(err);
      setPin("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-cosmic-navy via-[#1a1f52] to-cosmic-purple p-4">
      <form onSubmit={submit} className="w-full max-w-sm space-y-5 rounded-3xl bg-white p-7 shadow-cosmic">
        <div className="flex justify-end"><LanguageSwitch /></div>
        <div className="flex justify-center"><Logo variant="full" className="h-40" /></div>
        <div>
          <h1 className="text-sm font-black">{t("login.title")}</h1>
          <p className="text-xs text-gray-500">{t("login.subtitle")}</p>
        </div>
        {isV2() && (
          <div>
            <label className="label" htmlFor="tenant">{t("login.tenant")}</label>
            <input id="tenant" className="field" autoComplete="organization" value={tenant} onChange={(e) => setTenant(e.target.value)} required />
            <p className="mt-1 text-[11px] text-gray-400">{t("login.tenantHint")}</p>
          </div>
        )}
        <div>
          <label className="label" htmlFor="username">{t("login.username")}</label>
          <input id="username" className="field" autoComplete="username" autoFocus value={username} onChange={(e) => setUsername(e.target.value)} required />
        </div>
        <div>
          <label className="label" htmlFor="pin">{t("login.pin")}</label>
          <input id="pin" className="field num font-mono" type="password" inputMode="numeric" autoComplete="current-password" value={pin} onChange={(e) => setPin(e.target.value)} required minLength={4} />
        </div>
        <button type="submit" className="btn-primary w-full !py-3 text-sm" disabled={busy || !username || pin.length < 4 || (isV2() && !tenant.trim())}>
          {busy ? t("common.loading") : t("login.submit")}
        </button>
      </form>
    </div>
  );
}
